import {HttpClient, HttpParams, HttpResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {AppConfigService} from '../core/config/app-config.service';
import {Observable} from 'rxjs';
import {Employee} from '../admin/models/employee.model';
import {DatePipe} from '@angular/common';
import {Team} from '../admin/models/team.model';
import {WorkAbroad} from '../admin/models/workAbroad.model';
import {Vacation} from '../admin/models/vacation.model';
import {AbsenceService} from '../shared/globals/absence-service.service';
import {AdUser} from '../admin/models/ad-user';
import {AuthenticationService} from '../core/authentication/authentication.service';

@Injectable({
    providedIn: 'root'
})
export class ManagerService {

    private readonly baseUrl;
    private readonly header: any;

    constructor(private readonly httpClient: HttpClient,
                private readonly configService: AppConfigService,
                private readonly authenticationService: AuthenticationService,
                private readonly absenceService: AbsenceService) {
        this.baseUrl = configService.getConfig().baseUrl;
        this.header = {'Authorization': 'Bearer ' + this.authenticationService.getAccessToken()};
    }

    /**
     * makes a call to the people service api to search for employees
     * @param searchQuery the data passed to be searched
     */
    public searchForEmployees(searchQuery: any): Observable<HttpResponse<AdUser[]>> {
        const params:
            HttpParams = new HttpParams()
            .set('query', searchQuery ?? '');

        return this.httpClient.get<AdUser[]>(this.baseUrl + '/people/services/search', {
            params,
            observe: 'response',
            headers: this.header
        });
    }

    /**
     * Fetches an team details by their gmin
     * @param gmin The gmin to get details for
     */
    public fetchTeamIdByGmin(gmin: string): Observable<Team[]> {
        return this.absenceService.getRequest(this.baseUrl, '/team/manager/' + gmin);
    }

    /**
     * Fetches all employees details by their teamId
     * @param teamId The teamId to get details for
     */
    public fetchAllEmployeeDetailsByTeamId(teamId: string | undefined): Observable<Employee[]> {
        return this.absenceService.getRequest(this.baseUrl, '/employee/team/' + teamId);
    }

    /**
     * Returns a string of date in the desired format
     * @param dateString The datestring to convert
     */
    public convertTimestamp(dateString: string): string {
        const newDateString = new DatePipe('en-US').transform(dateString, 'dd/MM/yyyy');
        return newDateString ? newDateString : '';
    }

    /**
     * Returns a string with the year only
     * @param dateString The datestring to split
     */
    public getYear(dateString: string): string {
        return dateString.substring(6, 10);
    }

    /**
     * Returns a string saying Yes or No given a boolean value
     * @param bool The boolean to check
     */
    public halfDayStatus(bool: boolean): string {
        if (bool) {
            return 'Yes'
        } else {
            return 'No'
        }
    }

    /**
     * Gets an array and returns an array of only unique values from it
     * @param array The array to search for unique values in
     */
    public getUniqueValues(array: any[]): any[] {
        return array.filter((value, index, self) => self.indexOf(value) === index);
    }

    /**
     * Makes an api call to approve the vacation request in the backend
     * @param data the vacation to be approved
     */
    public approveVacationRequest(data: Vacation): Observable<null> {
        return this.absenceService.putRequest(this.baseUrl, '/vacation', data);
    }

    public requestDeclined(data: Vacation): Observable<null> {
        return this.absenceService.putRequest(this.baseUrl, '/vacation/declined', data);
    }

    /**
     * Makes an api call to decline a vacation
     * @param data the vacation to be declined
     */
    public declineVacationRequest(data: Vacation): Observable<null> {
        return this.absenceService.putRequest(this.baseUrl, '/vacation', data);
    }

    /**
     * Makes an api call to delete a vacation
     * @param id the vacation id to be declined
     */
    public deleteVacationRequest(id: string): Observable<null> {
        return this.absenceService.deleteRequest(this.baseUrl, '/vacation/cancel/' + id);
    }

    /**
     * Makes an api call to approve a work abroad request
     * @param data a work abroad object
     */
    public approveWorkRequest(data: WorkAbroad): Observable<null> {
        return this.absenceService.putRequest(this.baseUrl, '/work-abroad', data);
    }

    /**
     * Makes an api call to decline a work abroad request
     * @param data a work abroad object
     */
    public declineWorkRequest(data: string): Observable<null> {
        return this.absenceService.putRequest(this.baseUrl, '/work-abroad', data);
    }

    public getData(data: Employee[], nameOfTeam: Map<string, string>): any {
        const pendingVacation: any[] = [];
        const allVacationRequest: any[] = [];
        const pendingWork: any[] = [];
        const allWorkRequest: any[] = [];
        const approvedVacation: any[] =[];
        data.forEach((value) => {
            pendingVacation.push(...this.vacationRequest(value, nameOfTeam));
            allVacationRequest.push(...this.allVacationRequest(value, nameOfTeam));
            pendingWork.push(...this.workAbroadRequest(value, nameOfTeam));
            allWorkRequest.push(...this.allWorkAbroadRequest(value, nameOfTeam));
            approvedVacation.push(...this.vacationRequest(value, nameOfTeam));
        });
        return {pendingVacation, allVacationRequest, pendingWork, allWorkRequest, approvedVacation};
    }

    /**
     * This method gets the pending requests for the vacation table from each employee
     * @param data An employee object
     * @param nameOfTeam Map to determine team name
     */
    public vacationRequest(data: Employee, nameOfTeam: Map<string, string>): any[] {
        const pendingVacation: any = [];
        data.vacation.forEach((dat: any) => {
            if (data.vacation.length > 0) {
                pendingVacation.push({
                    id: dat.id,
                    firstName: data.firstName,
                    lastName: data.lastName,
                    dateCreated: dat.dateCreated,
                    halfDay: dat.halfDay,
                    halfDayType: dat.halfDayType,
                    startDate: dat.startDate,
                    endDate: dat.endDate,
                    numberOfAnnualLeaveDays: dat.numberOfAnnualLeaveDays,
                    requestStatus: dat.requestStatus,
                    absenceReason: dat.absenceReason,
                    employeeId: dat.employeeId,
                    region: dat.region,
                    createdBy: dat.createdBy,
                    createdTimestamp: dat.createdTimestamp,
                    teamName: nameOfTeam.get(data.teamId),
                    absenceType: dat.absenceType,
                    year: this.getYear(dat.endDate),
                    gmin: data.gmin
                });
            }
        });
        return pendingVacation;
    }

    /**
     * This method gets all vacation requests
     * @param data Employee
     * @param nameOfTeam Map to determine team name
     */
    public allVacationRequest(data: Employee, nameOfTeam: Map<string, string>): any[] {
        const allVacationRequest: any[] = [];
        let workAllowanceRemaining: number = 0
        let workTotal: number = 0
        data.employeeWorkAbroadAllowance.forEach((wok: any) => {
            workAllowanceRemaining = wok.allowanceRemaining;
            workTotal = wok.allowance;
        });
        data.employeeVacationAllowance.forEach((dat: any) => {
            allVacationRequest.push({
                firstName: data.firstName,
                lastName: data.lastName,
                gmin: data.gmin,
                email: data.emailAddress,
                startDate: data.dateStarted,
                contractType: data.contractType,
                location: data.location,
                vacationAllowance: dat.vacationAllowance,
                holidayTaken: (dat.vacationAllowance + dat.vacationAllowanceCarryover) - dat.vacationAllowanceRemaining,
                vacationAllowanceRemaining: dat.vacationAllowanceRemaining,
                vacationAllowanceCarryover: dat.vacationAllowanceCarryover,
                year: dat.year,
                teamName: nameOfTeam.get(data.teamId),
                workAbroadAllowanceRemaining: workAllowanceRemaining,
                workAllowanceTotal: workTotal,
                employeeYear: this.getYear(data.dateStarted)
            });
        });
        return allVacationRequest
    }

    public cancelVacationRequest(data: Employee, nameOfTeam: Map<string, string>): any[] {
        const approvedVacation: any = [];
        data.vacation.forEach((dat: any) => {
            if (data.vacation.length > 0) {
                approvedVacation.push({
                    id: dat.id,
                    firstName: data.firstName,
                    lastName: data.lastName,
                    dateCreated: dat.dateCreated,
                    halfDay: dat.halfDay,
                    startDate: dat.startDate,
                    endDate: dat.endDate,
                    numberOfAnnualLeaveDays: dat.numberOfAnnualLeaveDays,
                    requestStatus: dat.requestStatus,
                    absenceReason: dat.absenceReason,
                    employeeId: dat.employeeId,
                    region: dat.region,
                    createdBy: dat.createdBy,
                    createdTimestamp: dat.createdTimestamp,
                    teamName: nameOfTeam.get(data.teamId),
                    absenceType: dat.absenceType,
                    year: this.getYear(dat.endDate),
                    gmin: data.gmin
                });
            }
        });
        return approvedVacation;
    }

    /**
     * This method gets all pending work abroad requests
     * @param data Employee object to loop through
     * @param nameOfTeam Map to determine team name
     */
    public workAbroadRequest(data: Employee, nameOfTeam: Map<string, string>): any[] {
        const pendingWork: any[] = [];
        let numberOfAnnualLeave = 0
        data.workAbroad.forEach((dat: any) => {
            numberOfAnnualLeave = 0;
            if (data.workAbroad.length > 0) {
                if (dat.vacations) {
                    dat.vacations.forEach((value: any) => {
                        numberOfAnnualLeave += value.numberOfAnnualLeaveDays;
                    });
                }
                pendingWork.push({
                    firstName: data.firstName,
                    lastName: data.lastName,
                    country: dat.country,
                    dateCreated: dat.dateCreated,
                    startDate: dat.startDate,
                    endDate: dat.endDate,
                    numberOfWorkAbroadDays: dat.numberOfWorkAbroadDays,
                    requestStatus: dat.requestStatus,
                    lastDayWorking: dat.lastDayWorking,
                    firstDayBack: dat.firstDayBack,
                    vacations: dat.vacations,
                    employeeId: dat.employeeId,
                    updatedBy: dat.updatedBy,
                    id: dat.id,
                    createdTimestamp: dat.createdTimestamp,
                    teamName: nameOfTeam.get(data.teamId),
                    year: this.getYear(dat.endDate),
                    numberOfAnnualLeaveDays: numberOfAnnualLeave,
                    gmin: data.gmin
                });
            }
        });
        return pendingWork;
    }

    /**
     * This method gets all workAbroad requests
     * @param data Employee object to loop through
     * @param nameOfTeam Map to determine team name
     */
    public allWorkAbroadRequest(data: Employee, nameOfTeam: Map<string, string>): any[] {
        const allWorkRequest: any[] = [];
        data.employeeWorkAbroadAllowance.forEach((dat: any) => {
            allWorkRequest.push({
                firstName: data.firstName,
                lastName: data.lastName,
                gmin: data.gmin,
                contractType: data.contractType,
                allowance: dat.allowance,
                workTaken: dat.allowance - dat.allowanceRemaining,
                allowanceRemaining: dat.allowanceRemaining,
                allowanceNegativeCarryover: dat.allowanceNegativeCarryover,
                year: String(dat.rollingPeriodStart),
                teamName: nameOfTeam.get(data.teamId)
            });
        });
        return allWorkRequest
    }


    /**
     * This method calculate the vacation requests to be populated in vacation card
     * @param data Employee object to loop through
     * @param team list of team name
     * @param year particular year
     */
    public calculateDisplayValueOfVacation(data: any[], year: string, team: string[]) {
        let annualLeave = 0;
        let remainingDays = 0;
        let daysUsed = 0;
        data.forEach((d) => {
            if (d.year === Number(year) && team.length === 0 || d.year === Number(year) && d.teamName === team) {
                annualLeave += Number(d.vacationAllowance + d.vacationAllowanceCarryover);
                remainingDays += Number(d.vacationAllowanceRemaining);
                daysUsed += Number(d.holidayTaken);
            }
        });
        return {annualLeave, remainingDays, daysUsed};
    }

    /**
     * This method calculate the vacation requests to be populated in mat card
     * @param data Employee object to loop through
     * @param team list of team name
     * @param year particular year
     */
    public calculatePendingDaysOfVacation(data: any[], year: string, team: string[]) {
        let pendingDays = 0;
        let pendingRequests = 0;
        data.forEach((val) => {
            if (team.length == 0 || team == val.teamName) {
                pendingDays += Number(val.numberOfAnnualLeaveDays);
                pendingRequests++;
            }
        });
        return {pendingDays, pendingRequests};
    }

    /**
     * This method calculate the work abroad requests to be populated in vacation card
     * @param data Employee object to loop through
     * @param year
     * @param team list of team name
     */
    public calculateDisplayOfWork(data: any[], year: string, team: string[]) {
        let annualWork = 0;
        let remainingWorkDays = 0;
        let workDaysUsed = 0;
        data.forEach((d) => {
            if (team.length == 0 || team == d.teamName) {
                annualWork += Number(d.allowance);
                remainingWorkDays += Number(d.allowanceRemaining);
                workDaysUsed += Number(d.workTaken);
            }
        });
        return {annualWork, remainingWorkDays, workDaysUsed};
    }


    /**
     * This method calculate the pending work abroad days requests for work abroad.
     * @param data Employee object to loop through
     * @param team list of team names
     * @param year year to be calculated
     */
    public calculatePendingDaysOfWork(data: any[], year: string, team: string[]) {
        let pendingWorkDays = 0;
        let pendingRequests = 0;
        data.forEach((val) => {
            if (team.length == 0 || team == val.teamName) {
                pendingWorkDays += Number(val.numberOfWorkAbroadDays);
                pendingRequests++;
            }
        });
        return {pendingWorkDays, pendingRequests};
    }

    /**
     * Gets an array of full names and gmin values from a datasources data
     * @param dataSourceData is the datasources data to be filtered
     * @returns a array of objects containing full names and gmin values.
     */
    public getEmployeeNameArray(dataSourceData: any[]): any[] {
        const nameArray: any[] = [];
        dataSourceData.forEach((value) => {
            nameArray.push({
                name: value.firstName.toLowerCase() + ' ' + value.lastName.toLowerCase(),
                gmin: value.gmin
            });
        });
        return nameArray;
    }

    /**
     * Filters the dataSource to only include values including the filterValue.
     * The nameArray is used to filter the combined first and last name fields,
     * and the separate gmin field.
     * @param filterValue Value to filter dataSource to
     * @param nameArray Array of full names and gmin values
     * @param dataSource Datasource tp be filtered
     */
    public filterDataSource(filterValue: string, nameArray: any[], dataSource: any[]): any[] {
        const filteredGminArray: string[] = [];
        nameArray.forEach((value: any) => {
            if ((value.name.includes(filterValue.toLowerCase()) || value.gmin.includes(filterValue)) && !filteredGminArray.includes(value.gmin)) {
                filteredGminArray.push(value.gmin);
            }
        });
        // The filteredValueArray is separate and uses a forEach rather than using a filter as returning from
        // inside the second foreach would return an array, not just the desired employee value.
        const filteredValueArray: any[] = [];
        dataSource.forEach((value: any) => {
            filteredGminArray.forEach((element) => {
                if (element === value.gmin && !filteredValueArray.includes(value)) {
                    filteredValueArray.push(value);
                }
            });
        });
        return filteredValueArray;
    }
}
